import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiMoreVertical } from "react-icons/fi";
import { FaPen, FaTrash, FaShareAlt } from "react-icons/fa";
import axios from "axios";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import NavButtons from "./NavButtons";
import Loader from "./Loader";
import SwipeContainer from "../utils/SwipeContainer";
import {
  ensureValidToken,
  isOfflineMode,
  OFFLINE_CACHE_KEYS,
  cacheData,
  getCachedData,
} from "../utils/authService";
import "../styles/Homemycards.css";

const Homemycards = () => {
  const [menuOpen, setMenuOpen] = useState(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("my-cards");
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditOptions, setShowEditOptions] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // New state for delete confirmation
  const [cardToDelete, setCardToDelete] = useState(null); // Store card to delete
  const location = useLocation();
  const [offline, setOffline] = useState(isOfflineMode());

  const MAX_CARDS = 3;

  const fetchCards = useCallback(async () => { 
    // Offline mode: use cached data only, no API calls
    if (isOfflineMode()) {
      const cached = getCachedData(OFFLINE_CACHE_KEYS.myCards, []);
      setCards(cached);
      setShowModal(false);
      setOffline(true);
      setLoading(false);
      return;
    }

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      const response = await axios.get("https://api.nexcard.co.in/api/cards/me/", {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      const results = response.data.results || [];
      setCards(results);
      cacheData(OFFLINE_CACHE_KEYS.myCards, results);
      const hasNoCards = results.length === 0;
      setShowModal(hasNoCards);
      if (!hasNoCards && location.state?.from?.includes('/shared-card/')) {
        navigate(location.state.from);
      }
    } catch (error) {
      console.error("Error fetching cards:", error);
      setCards([]);
      setShowModal(true);
    } finally {
      setLoading(false);
      setOffline(false);
    }
  }, [location.state, navigate]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleClick = (message) => {
    alert(message);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (cardId, e) => {
    e.stopPropagation();

    if (isOfflineMode()) {
      alert("You are offline. Deleting cards is disabled in offline mode.");
      return;
    }

    setCardToDelete(cardId);
    setShowDeleteModal(true);
    setMenuOpen(null); // Close the menu when delete is clicked
  };

  // Confirm and execute deletion
  const confirmDelete = async () => {
    if (!cardToDelete) return;

    if (isOfflineMode()) {
      alert("You are offline. Deleting cards is disabled in offline mode.");
      return;
    }

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      await axios.delete(`https://api.nexcard.co.in/api/cards/${cardToDelete}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      // Refresh the cards list
      fetchCards();
      
      // Close the modal and reset state
      setShowDeleteModal(false);
      setCardToDelete(null);
    } catch (error) {
      console.error("Error deleting card:", error);
      alert("Failed to delete card. Please try again.");
      setShowDeleteModal(false);
      setCardToDelete(null);
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCardToDelete(null);
  };

  const toggleMenu = (index) => {
    setMenuOpen(menuOpen === index ? null : index);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const handleCardClick = (cardId) => {
    navigate(`/card-details/${cardId}`);
  };

  const handleEditClick = (cardId, e) => {
    e.stopPropagation();
    setSelectedCardId(cardId);
    setShowEditOptions(true);
  };

  const handleEditOption = (option) => {
    if (isOfflineMode()) {
      alert("You are offline. Editing cards is disabled in offline mode.");
      return;
    }

    if (option === 'upload') {
      navigate(`/edit-card/${selectedCardId}`);
    } else {
      navigate(`/edit-card/${selectedCardId}/step2`);
    }
    setShowEditOptions(false);
  };

  const handleAddNewCard = () => {
    if (isOfflineMode()) {
      alert("You are offline. Creating new cards is disabled in offline mode.");
      return;
    }

    if (cards.length >= MAX_CARDS) {
      setShowLimitModal(true);
    } else {
      navigate("/add-new-card");
    }
  };

  const handleShare = (cardId, e) => {
    e.stopPropagation();
    
    // Find the card to get the owner's name
    const card = cards.find(c => c.id === cardId);
    const ownerName = card?.full_name || 'Someone';
    
    const shareUrl = `${window.location.origin}/shared-card/${cardId}`;
    const shareText = `Hey! 👋\nHere's the digital business card of *${ownerName}* —\n🔗 Click the link to Open:`;
    
    if (navigator.share) {
      navigator.share({
        title: `${ownerName}'s Business Card`,
        text: shareText,
        url: shareUrl
      });
    } else if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      // For mobile devices without native share, include URL in text for WhatsApp
      const whatsappText = `${shareText} ${shareUrl}`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
      window.open(whatsappUrl);
    } else {
      // For desktop, include URL in the clipboard text
      const clipboardText = `${shareText} ${shareUrl}`;
      navigator.clipboard.writeText(clipboardText);
      alert("Message with link copied to clipboard!");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <Header toggleSidebar={toggleSidebar} handleClick={handleClick} />
      <NavButtons activeTab={activeTab} setActiveTab={setActiveTab} />

      <SwipeContainer>
        <div className="main-content">
          {activeTab === "my-cards" && (
            <>
              {offline && (
                <div className="offline-banner">
                  You are in offline mode. Showing last saved My Cards.
                </div>
              )}
              <div className="latest-created">
                <p>Latest Created Cards</p>
                <button
                  className="add-card-btn1"
                  onClick={handleAddNewCard}
                >
                  Add New Card
                </button>
              </div>

              {cards.length === 0 ? (
                <div className="empty-state4">
                  <h3>You have no cards yet. Create one now!</h3>
                </div>
              ) : (
                <div className="cards-list">
                  {cards.map((card, index) => (
                    <div key={card.id} className="card" onClick={() => handleCardClick(card.id)}>
                      <div className="card-header1">
                        <button className="menu-btn" onClick={(e) => { e.stopPropagation(); toggleMenu(index); }}>
                          <FiMoreVertical size={20} />
                        </button>
                      </div>
                      <div className="card-image">
                        {card.file?.file.endsWith('.pdf') ? (
                          <div className="pdf-preview">
                            <div className="pdf-icon">PDF</div>
                            <span>{card.file?.name || 'Document'}</span>
                          </div>
                        ) : (
                          <img src={card.file?.file} alt="Business Card" />
                        )}
                      </div>
                      {menuOpen === index && (
                        <div className="dropdown-menu" ref={menuRef} onClick={(e) => e.stopPropagation()}>
                          <button 
                            className="menu-item" 
                            onClick={(e) => handleEditClick(card.id, e)}
                          >
                            <FaPen /> Edit
                          </button>
                          <button 
                            className="menu-item delete-btn" 
                            onClick={(e) => handleDeleteClick(card.id, e)}
                          >
                            <FaTrash /> Delete
                          </button>
                          <button 
                            className="menu-item" 
                            onClick={(e) => handleShare(card.id, e)}
                          >
                            <FaShareAlt /> Share
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </SwipeContainer>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay4">
          <div className="modal4">
            <h2>Delete Card</h2>
            <p>Are you sure you want to delete this card?</p>
            <p className="delete-warning">This action cannot be undone.</p>
            <div className="delete-modal-buttons">
              <button
                className="cancel-delete-btn"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Options Modal */}
      {showEditOptions && (
        <div className="modal-overlay4">
          <div className="modal4">
            <h2>Edit Options</h2>
            <p>Choose how you want to edit this card</p>
            <div className="edit-options-buttons">
              <button
                className="edit-option-btn"
                onClick={() => handleEditOption('upload')}
              >
                Upload & Edit Card
              </button>
              <button
                className="edit-option-btn"
                onClick={() => handleEditOption('details')}
              >
                Edit Card Details 
              </button>
            </div>
            <button
              className="cancel-edit-btn"
              onClick={() => setShowEditOptions(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Card Limit Reached Modal */}
      {showLimitModal && (
        <div className="modal-overlay4">
          <div className="modal4">
            <h2>Card Limit Reached</h2>
            <p>You can only create up to {MAX_CARDS} cards.</p>
            <p>Please delete an existing card to create a new one.</p>
            <button
              className="cancel-edit-btn"
              onClick={() => setShowLimitModal(false)}
            >
              Okay
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay4">
          <div className="modal4">
            <div className="modal-header4">
              <h2>Add Your First Card</h2>
              <button
                className="modal-close-btn4"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>
            <p>You have no cards yet. Create one now!</p>
            <button
              className="add-card-btn2"
              onClick={() => {
                navigate("/add-new-card", { 
                  state: {
                    afterAddRedirect: location.state?.from || "/home-my-cards",
                    sharedCardId: location.state?.sharedCardId
                  }
                });
              }}
            >
              Add New Card
            </button>
          </div>
        </div>
      )}

      <Footer handleClick={handleClick} />
    </>
  );
};

export default Homemycards;